import React, { createContext, useContext, useMemo, useState } from "react";
import type { Customer } from "./customers";

// "Shopping as …" — a session-level choice, peer to the cart. Not
// persisted: a demo visitor picks who they are for this session.
//
// Two distinct pools, not one merged list (#304): `self` — the signed-in
// identity, a real unseeded account that order-service auto-upserts a
// profile row for on login (ADR 0059) — defaults to selected, so checking
// out "as yourself" needs no picking at all. `personas` — the 50 seeded
// demo customers (ADR 0036, not a signup) — stay fully selectable for
// exercising real credit-limit / fraud-velocity history a fresh account
// won't have.

interface CustomerApi {
  self: Customer;
  personas: Customer[];
  selected: Customer;
  select: (customerId: string) => void;
}

const CustomerContext = createContext<CustomerApi | null>(null);

export function CustomerProvider({
  self,
  personas,
  children,
}: {
  self: Customer;
  personas: Customer[];
  children: React.ReactNode;
}) {
  const [selectedId, setSelectedId] = useState<string>(self.customerId);

  const api = useMemo<CustomerApi>(() => {
    const combined = [self, ...personas];
    return {
      self,
      personas,
      selected: combined.find((c) => c.customerId === selectedId) ?? self,
      select: setSelectedId,
    };
  }, [self, personas, selectedId]);

  return <CustomerContext.Provider value={api}>{children}</CustomerContext.Provider>;
}

export function useCustomer(): CustomerApi {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer outside CustomerProvider");
  return ctx;
}
